import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, AlertTriangle, ArrowRight, Home } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// Selcom sometimes takes up to ~45s to fire its webhook back to us.
// Poll every 2s for up to 60s, then let the user manually refresh.
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_MS = 60000;

export default function PaymentCallback() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const orderId = params.get('order_id') || params.get('order') || params.get('orderid');
  const selcomStatus = params.get('status'); // "cancelled" | "failed" | undefined

  const [state, setState] = useState('pending'); // pending | paid | expired | error | cancelled
  const [detail, setDetail] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    // Selcom sometimes returns a `status=cancelled` marker in the redirect URL.
    if (selcomStatus && ['cancelled', 'canceled', 'failed'].includes(selcomStatus.toLowerCase())) {
      setState('cancelled');
      return;
    }
    if (!orderId) {
      setState('error');
      setDetail({ message: 'Missing order_id in the callback URL.' });
      return;
    }
    if (!token) {
      // If unauthed (redirect landed before session restored), send to login and preserve intent.
      sessionStorage.setItem('wekeza_next', `/payment/selcom/callback${search}`);
      navigate('/login', { replace: true });
      return;
    }

    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/payments/selcom/subscription-status?order_id=${encodeURIComponent(orderId)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        setDetail(data);
        if (data.status === 'paid') {
          setState('paid');
          return; // stop polling
        }
        if (['failed', 'cancelled', 'canceled'].includes((data.status || '').toLowerCase())) {
          setState('cancelled');
          return;
        }
        // still pending — check clock
        const el = Date.now() - startedAt.current;
        setElapsed(el);
        if (el >= POLL_MAX_MS) {
          setState('expired');
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          // Selcom may webhook a moment later, keep polling within the window
          const el = Date.now() - startedAt.current;
          if (el >= POLL_MAX_MS) {
            setState('expired');
          } else {
            setElapsed(el);
            setTimeout(tick, POLL_INTERVAL_MS);
          }
          return;
        }
        setState('error');
        setDetail({ message: err?.response?.data?.detail || err.message });
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [orderId, selcomStatus, token, navigate, search]);

  const progressPct = Math.min(100, Math.round((elapsed / POLL_MAX_MS) * 100));

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-white flex items-center justify-center px-4 py-10">
      <div
        className="w-full max-w-md bg-white rounded-3xl shadow-lg border border-slate-100 p-8"
        data-testid="payment-callback-card"
      >
        {state === 'pending' && (
          <div className="text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
              <Loader2 className="w-7 h-7 text-emerald-700 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mt-4">
              {t('Confirming your payment…', 'Tunathibitisha malipo yako…')}
            </h1>
            <p className="text-sm text-slate-600 mt-2">
              {t(
                'We are waiting for Selcom to confirm the TZS 15,000 charge. This usually takes 10–45 seconds.',
                'Tunasubiri Selcom kuthibitisha malipo ya TZS 15,000. Kwa kawaida huchukua sekunde 10 hadi 45.',
              )}
            </p>
            <div className="mt-6 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-600 transition-all"
                style={{ width: `${progressPct}%` }}
                data-testid="payment-callback-progress"
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {t('Order', 'Oda')}: <code className="text-[11px]">{orderId || '—'}</code>
            </p>
          </div>
        )}

        {state === 'paid' && (
          <div className="text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-700" />
            </div>
            <h1 className="text-2xl font-bold text-emerald-800 mt-4" data-testid="payment-callback-success">
              {t('Premium activated!', 'Premium imewashwa!')}
            </h1>
            <p className="text-sm text-slate-700 mt-2">
              {t(
                'Selcom confirmed your payment. Your Premium plan is live for the next 30 days.',
                'Selcom imethibitisha malipo yako. Premium yako inatumika kwa siku 30 zijazo.',
              )}
            </p>
            {detail?.paid_at && (
              <p className="text-xs text-slate-500 mt-2">
                {t('Paid at', 'Malipo yamefanyika')}: {new Date(detail.paid_at).toLocaleString()}
              </p>
            )}
            <Button
              onClick={() => navigate('/dashboard')}
              className="mt-6 w-full h-11 rounded-xl bg-emerald-700 hover:bg-emerald-800"
              data-testid="payment-callback-continue-btn"
            >
              {t('Open my dashboard', 'Fungua dashibodi yangu')} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {state === 'cancelled' && (
          <div className="text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-rose-100 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-rose-700" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mt-4" data-testid="payment-callback-cancelled">
              {t('Payment cancelled', 'Malipo yamesitishwa')}
            </h1>
            <p className="text-sm text-slate-700 mt-2">
              {t(
                'No charge was made. You can try again anytime — your Premium waits for you.',
                'Hakuna gharama iliyofanyika. Unaweza kujaribu tena wakati wowote — Premium yako inakusubiri.',
              )}
            </p>
            <div className="mt-6 grid gap-2">
              <Button
                onClick={() => navigate('/?upgrade=premium#pricing')}
                className="w-full h-11 rounded-xl bg-emerald-700 hover:bg-emerald-800"
                data-testid="payment-callback-retry-btn"
              >
                {t('Try Premium again', 'Jaribu Premium tena')}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="w-full h-11 rounded-xl"
              >
                <Home className="w-4 h-4 mr-1" /> {t('Back to dashboard', 'Rudi dashibodi')}
              </Button>
            </div>
          </div>
        )}

        {state === 'expired' && (
          <div className="text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-amber-700" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mt-4" data-testid="payment-callback-expired">
              {t('Still confirming with Selcom…', 'Bado tunathibitisha na Selcom…')}
            </h1>
            <p className="text-sm text-slate-700 mt-2">
              {t(
                'Selcom is taking longer than usual. If you approved the charge on your phone, refresh in a minute — your Premium will activate automatically. Nothing was charged twice.',
                'Selcom inachukua muda mrefu kuliko kawaida. Kama uliidhinisha malipo kwenye simu, jaribu tena baada ya dakika — Premium itawashwa kiotomatiki. Hakuna malipo mara mbili.',
              )}
            </p>
            <div className="mt-6 grid gap-2">
              <Button
                onClick={() => window.location.reload()}
                className="w-full h-11 rounded-xl bg-emerald-700 hover:bg-emerald-800"
                data-testid="payment-callback-refresh-btn"
              >
                {t('Refresh status', 'Angalia hali tena')}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="w-full h-11 rounded-xl"
              >
                <Home className="w-4 h-4 mr-1" /> {t('Back to dashboard', 'Rudi dashibodi')}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              {t('Order', 'Oda')}: <code className="text-[11px]">{orderId || '—'}</code>
            </p>
          </div>
        )}

        {state === 'error' && (
          <div className="text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-rose-100 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-rose-700" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mt-4" data-testid="payment-callback-error">
              {t('Something went wrong', 'Kuna hitilafu')}
            </h1>
            <p className="text-sm text-slate-700 mt-2">
              {detail?.message
                || t('We could not check the payment status right now. Please try again in a minute.',
                     'Tumeshindwa kuangalia hali ya malipo sasa. Jaribu tena baada ya dakika.')}
            </p>
            <div className="mt-6 grid gap-2">
              <Button
                onClick={() => window.location.reload()}
                className="w-full h-11 rounded-xl bg-emerald-700 hover:bg-emerald-800"
              >
                {t('Try again', 'Jaribu tena')}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="w-full h-11 rounded-xl"
              >
                <Home className="w-4 h-4 mr-1" /> {t('Back to dashboard', 'Rudi dashibodi')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
